import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { trackConversion } from "@/lib/track";
import { SERVICES, SITE } from "@/data/site";
import { Calendar, Clock, CheckCircle2, MessageCircle } from "lucide-react";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";

const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00","22:00"];

export default function AppointmentDialog({ open, onOpenChange, defaultService }) {
  const [loading, setLoading] = useState(false);
  const [waLink, setWaLink] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: defaultService || "",
    date: "",
    time: "",
    note: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleOpenChange = (v) => {
    if (!v) setWaLink(null);
    onOpenChange(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.date || !form.time) {
      toast.error("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      trackConversion();
      const niceDate = format(parseISO(form.date), "d MMMM yyyy, EEEE", { locale: tr });
      const waText = `Merhaba, web sitenizden randevu talebi oluşturdum.\n\nİsim: ${form.name}\nTelefon: ${form.phone}\nHizmet: ${form.service}\nTarih: ${niceDate}\nSaat: ${form.time}${form.note ? `\nNot: ${form.note}` : ""}`;
      setWaLink(`https://wa.me/${SITE.whatsappRaw}?text=${encodeURIComponent(waText)}`);
      toast.success("Randevunuz alındı. En kısa sürede sizinle iletişime geçilecek.");
      setForm({ name: "", phone: "", service: defaultService || "", date: "", time: "", note: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Bir hata oluştu, lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="appointment-dialog"
        className="bg-bone border border-pv text-ink max-w-xl p-0 overflow-hidden rounded-sm"
      >
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          {waLink ? (
            <div className="px-8 py-12 text-center" data-testid="appt-success-panel">
              <DialogHeader className="text-center sm:text-center items-center">
                <CheckCircle2 className="w-14 h-14 text-bronze mx-auto" />
                <DialogTitle className="font-display text-3xl text-forest-deep font-normal mt-5">
                  Randevunuz <span className="pv-italic text-bronze">alındı</span>
                </DialogTitle>
                <DialogDescription className="text-ink-soft font-light text-sm mt-2 leading-relaxed">
                  Talebiniz bize ulaştı. Hızlı onay için randevu detaylarınızı tek tıkla WhatsApp'tan iletin — ekibimiz anında dönüş yapsın.
                </DialogDescription>
              </DialogHeader>
              <a
                data-testid="appt-whatsapp-send"
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="pv-btn pv-btn-bronze w-full justify-center mt-7"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp'tan Gönder
              </a>
              <button
                data-testid="appt-close"
                onClick={() => handleOpenChange(false)}
                className="mt-4 text-[11px] tracking-luxury uppercase text-muted-pv hover:text-forest-deep transition-colors"
              >
                Kapat
              </button>
            </div>
          ) : (
          <>
          <div className="px-7 pt-8 pb-4 border-b border-pv-soft bg-cream-warm">
            <DialogHeader>
              <p className="text-[10px] tracking-luxury uppercase text-bronze mb-2">Randevu</p>
              <DialogTitle className="font-display text-3xl text-forest-deep font-normal">
                Bir <span className="pv-italic text-bronze">ritüel</span> rezerve edin
              </DialogTitle>
              <DialogDescription className="text-ink-soft/70 font-light text-sm">
                Formu doldurun, ekibimiz 30 dakika içinde sizinle iletişime geçsin.
              </DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={submit} className="px-7 py-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] tracking-luxury uppercase text-muted-pv mb-2 block">İsim *</Label>
                <Input
                  data-testid="appt-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="bg-cream-warm border-pv focus:border-forest rounded-none h-11 text-ink"
                  placeholder="Adınız Soyadınız"
                  required
                />
              </div>
              <div>
                <Label className="text-[11px] tracking-luxury uppercase text-muted-pv mb-2 block">Telefon *</Label>
                <Input
                  data-testid="appt-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="bg-cream-warm border-pv focus:border-forest rounded-none h-11 text-ink"
                  placeholder="05XX XXX XX XX"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-[11px] tracking-luxury uppercase text-muted-pv mb-2 block">Hizmet *</Label>
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="appt-service" className="bg-cream-warm border-pv focus:border-forest rounded-none h-11 text-ink">
                  <SelectValue placeholder="Bir ritüel seçin" />
                </SelectTrigger>
                <SelectContent className="bg-bone border-pv text-ink">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.title} className="focus:bg-cream-warm">
                      {s.title} · {s.duration}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] tracking-luxury uppercase text-muted-pv mb-2 block">Tarih *</Label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="appt-date"
                      className="w-full h-11 px-3 bg-cream-warm border border-pv hover:border-forest focus:border-forest focus:outline-none rounded-none text-sm text-left flex items-center justify-between gap-2 transition-colors"
                    >
                      <span className={form.date ? "text-ink" : "text-muted-pv font-light"}>
                        {form.date ? format(parseISO(form.date), "d MMMM yyyy, EEEE", { locale: tr }) : "Tarih seçin"}
                      </span>
                      <Calendar className="w-4 h-4 text-bronze shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-bone border-pv rounded-none shadow-xl" align="start">
                    <CalendarPicker
                      mode="single"
                      locale={tr}
                      weekStartsOn={1}
                      selected={form.date ? parseISO(form.date) : undefined}
                      onSelect={(d) => {
                        if (d) {
                          update("date", format(d, "yyyy-MM-dd"));
                          setDateOpen(false);
                        }
                      }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      classNames={{
                        day_selected:
                          "bg-forest text-cream hover:bg-forest hover:text-cream focus:bg-forest focus:text-cream rounded-none",
                        day_today: "border border-bronze/60 text-bronze-deep rounded-none",
                        day: "h-9 w-9 p-0 font-light text-ink hover:bg-cream-warm rounded-none aria-selected:opacity-100",
                        head_cell: "text-muted-pv w-9 font-normal text-[10px] uppercase tracking-wider",
                        caption_label: "text-sm font-medium text-forest-deep capitalize",
                        nav_button: "h-7 w-7 border-pv hover:bg-cream-warm rounded-none",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-[11px] tracking-luxury uppercase text-muted-pv mb-2 block">Saat *</Label>
                <Select value={form.time} onValueChange={(v) => update("time", v)}>
                  <SelectTrigger data-testid="appt-time" className="bg-cream-warm border-pv focus:border-forest rounded-none h-11 text-ink">
                    <SelectValue placeholder="Saat seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-bone border-pv text-ink max-h-72">
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-cream-warm">
                        <div className="flex items-center gap-2"><Clock className="w-3 h-3 text-bronze" /> {t}</div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[11px] tracking-luxury uppercase text-muted-pv mb-2 block">Not (opsiyonel)</Label>
              <Textarea
                data-testid="appt-note"
                value={form.note}
                onChange={(e) => update("note", e.target.value)}
                className="bg-cream-warm border-pv focus:border-forest rounded-none text-ink min-h-[80px]"
                placeholder="Tercihleriniz, özel istekleriniz..."
              />
            </div>

            <button
              data-testid="appt-submit"
              type="submit"
              disabled={loading}
              className="pv-btn pv-btn-solid w-full justify-center disabled:opacity-60"
            >
              {loading ? "Gönderiliyor..." : "Randevu Talebini Gönder"}
            </button>
          </form>
          </>
          )}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
